import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useProjectReport } from '../hooks/useOps';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';
import type { ProjectTrack, ProblemReport } from '../api/types';

interface ReportData {
  project: { id: string; code: string; name: string; status: string };
  generatedAt: string;
  overallPct: number;
  tracks: (ProjectTrack & { recentLogs: unknown[] })[];
  openProblems: ProblemReport[];
  deliveryMilestones: unknown[];
}

export function ReportPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useProjectReport(projectId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {(error as Error)?.message ?? 'Failed to generate report'}
      </Alert>
    );
  }

  const report = data as ReportData;

  return (
    <Box>
      <PageHeader
        title="Project Report"
        subtitle={`${report.project.code} — ${report.project.name}`}
        action={
          <Button variant="outlined" onClick={() => navigate(`/projects/${projectId}`)}>
            ← Project
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              Generated: {formatDate(report.generatedAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project status:{' '}
              <Chip
                size="small"
                label={humanizeStatus(report.project.status)}
                color={statusColor(report.project.status)}
                variant="outlined"
              />
            </Typography>
          </Stack>
          <Stack alignItems="flex-end">
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {report.overallPct}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              overall progress
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Tracks summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          Track status
        </Typography>
        <Stack gap={1} divider={<Divider />}>
          {report.tracks.map((track) => {
            const itemCount = track.items?.length ?? 0;
            const pct =
              track.progressPct ??
              (track.totalUnits > 0
                ? Math.round((track.currentUnits / track.totalUnits) * 100)
                : 0);
            return (
              <Stack key={track.id} direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  {track.label}
                </Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {itemCount > 0
                      ? `${pct}% · ${itemCount} sub-task${itemCount === 1 ? '' : 's'}`
                      : `${track.currentUnits} / ${track.totalUnits} ${track.unitLabel}`}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${pct}%`}
                    color={pct === 100 ? 'success' : 'default'}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={humanizeStatus(track.status)}
                    color={statusColor(track.status)}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            );
          })}
          {report.tracks.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              No tracks.
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Open problems */}
      {report.openProblems.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Open problems ({report.openProblems.length})
          </Typography>
          <Stack gap={1} divider={<Divider />}>
            {report.openProblems.map((p) => (
              <Stack key={p.id} direction="row" justifyContent="space-between" alignItems="flex-start" py={0.5}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {p.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.description}
                  </Typography>
                </Box>
                <Chip size="small" label={p.severity} variant="outlined" />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
